import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@mui/styles";
import { useState } from "react";

import {
  CANCEL_ALL_WARNING,
  CANCEL_ALL_WARNING_TITLE,
  MATRIX_MODES,
} from "../../../consts/strings";
import { DELIVERY_PLAN_TYPES } from "../../../consts/planTypes";
import { palette } from "../../../theme/palette";

import {
  getSelectedDepot,
  showOptimiseAllSelector,
} from "../../../reducers/ownApp/selectors";
import {
  depotLoadingSelector,
  depotLockSelector,
  mapEditModeSelector,
  partialPostcodesVersionsSelector,
} from "../../../reducers/depot/selectors";
import { hasMapAdminRoleSelector } from "../../../reducers/ownUser/selectors";
import { setEditMapModalOpen } from "../../../reducers/ownApp";
import {
  cancelAllEdits,
  optimiseDepotRoutes,
  savePlan,
  validateProfile,
} from "../../../reducers/matrixProfile/thunkActions";
import {
  overlayDetailsSelector,
  getMatrixProfileType,
  isEditOneModeSelector,
  isDailyPlanSelector,
  matrixModeSelector,
  matrixProfileLoadingSelector,
} from "../../../reducers/matrixProfile/selectors";

import { ActionButton } from "../Common/ActionButton";
import DepotSummary from "../../DepotSummary";
import LockComponent from "./LockComponent";
import { DriverlessRoutesModal } from "../../Modals/DriverlessRoutes/DriverlessRoutesModal";
import { UnallocatedPostcodesModal } from "../../Modals/UnallocatedPostcodes/UnallocatedPostcodesModal";
import { OverridesConflictsModal } from "../../Modals/OverridesConflictsModal/OverridesConflictsModal";
import { SingleWavesPostcodesModal } from "../../Modals/SingleWavesPostcodes/SingleWavesPostcodesModal";
import { ConfirmModal } from "../../Modals/ConfirmModal/ConfirmModal";
import { UnlockIcon } from "../../Icons";
import { View } from "../../View";

const useStyles = makeStyles(({ typography }) => ({
  container: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    height: typography.pxToRem(30),
    width: "100vw",
    backgroundColor: palette.transparent,
  },
  rightActions: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
  },
  leftContent: {
    display: "inline-block",
  },
  unlockIcon: {
    color: palette.typography.black.solid,
    width: "10px",
    height: "12px",
    marginRight: "3px",
  },
}));

export const Subheader = () => {
  const styles = useStyles();
  const hasMapAdminRole = useSelector(hasMapAdminRoleSelector);
  const mapEditMode = useSelector(mapEditModeSelector);
  const isDepotLoading = useSelector(depotLoadingSelector);
  const isMatrixLoading = useSelector(matrixProfileLoadingSelector);
  const selectedDepot = useSelector(getSelectedDepot);
  const overlayDetails = useSelector(overlayDetailsSelector);
  const matrixMode = useSelector(matrixModeSelector);
  const planType = useSelector(getMatrixProfileType);
  const isEditOneMode = useSelector(isEditOneModeSelector);
  const isDailyPlan = useSelector(isDailyPlanSelector);
  const showOptimiseAll = useSelector(showOptimiseAllSelector);
  const depotLock = useSelector(depotLockSelector);
  const partialPostcodesVersions = useSelector(
    partialPostcodesVersionsSelector
  );
  const dispatch = useDispatch();
  const [confirmCancelModalOpen, setConfirmCancelModalOpen] = useState(false);
  const [driverlessRoutes, setDriverlessRoutes] = useState();
  const [unallocatedPostcodes, setUnallocatedPostcodes] = useState();
  const [singleWavePostcodes, setSingleWavePostcodes] = useState();
  const [overrideConflictsPostcodes, setOverrideConflictsPostcodes] =
    useState();

  const onSaveClick = async ({
    ignoreDriverlessRoutes,
    ignoreUnallocatedPostcodes,
    ignoreSingleWavePostcodes,
  } = {}) => {
    const { payload } = await dispatch(validateProfile());
    if (!payload) return;

    const hasOverridesConflicts = !!payload.overridesConflictsPostcodes?.length;
    const hasUnallocatedPostcodes =
      !ignoreUnallocatedPostcodes && !!payload.unallocatedPostcodes?.length;
    const hasSingleWavePostcodes =
      !ignoreSingleWavePostcodes && !!payload.singleWavePostcodes?.length;
    const hasDriverlessRoutes =
      !ignoreDriverlessRoutes && !!payload.hasDriverlessRoutes;

    if (hasOverridesConflicts)
      setOverrideConflictsPostcodes(payload.overridesConflictsPostcodes);
    else if (hasUnallocatedPostcodes)
      setUnallocatedPostcodes(payload.unallocatedPostcodes);
    else if (hasSingleWavePostcodes)
      setSingleWavePostcodes(payload.singleWavePostcodes);
    else if (hasDriverlessRoutes) setDriverlessRoutes(payload.driverlessRoutes);
    else if (!isDailyPlan || payload.isValid) dispatch(savePlan());
  };

  const onModalClose = () => {
    setDriverlessRoutes(null);
    setUnallocatedPostcodes(null);
    setSingleWavePostcodes(null);
    setOverrideConflictsPostcodes(null);
  };

  const onUnallocatedPostcodesModalContinue = () => {
    onModalClose();
    onSaveClick({
      ignoreUnallocatedPostcodes: true,
    });
  };

  const onSingleWavePostcodesModalContinue = () => {
    onModalClose();
    onSaveClick({
      ignoreSingleWavePostcodes: true,
      ignoreUnallocatedPostcodes: true,
    });
  };

  const onDriverlessRoutesModalContinue = () => {
    onModalClose();
    onSaveClick({
      ignoreDriverlessRoutes: true,
      ignoreSingleWavePostcodes: true,
      ignoreUnallocatedPostcodes: true,
    });
  };

  const shouldShowOptimise = () =>
    DELIVERY_PLAN_TYPES.includes(planType) && showOptimiseAll && overlayDetails;

  const shouldShowEditMap = () =>
    !isDepotLoading &&
    !isMatrixLoading &&
    partialPostcodesVersions?.length < 2 &&
    selectedDepot &&
    matrixMode === MATRIX_MODES.NONE_SELECTED &&
    !mapEditMode &&
    hasMapAdminRole &&
    (!depotLock.lockedByUserCode ||
      (depotLock.lockedByYou && !depotLock.lockedInDifferentBrowser));

  return (
    <View className={styles.container}>
      <View className={styles.leftContent}>
        <DepotSummary />
      </View>
      <View className={styles.rightActions}>
        <LockComponent
          onCancelClick={() => setConfirmCancelModalOpen(true)}
          onSaveClick={() => onSaveClick()}
        />

        {shouldShowOptimise() && (
          <ActionButton
            disabled={isEditOneMode}
            onClick={() => {
              dispatch(optimiseDepotRoutes());
            }}
          >
            Optimise
          </ActionButton>
        )}
        {shouldShowEditMap() && (
          <ActionButton
            variant={"yellow"}
            disabled={false}
            onClick={() => {
              dispatch(setEditMapModalOpen(true));
            }}
          >
            <UnlockIcon className={styles.unlockIcon} />
            Edit Map
          </ActionButton>
        )}
      </View>
      <DriverlessRoutesModal
        open={!!driverlessRoutes}
        onClose={onModalClose}
        onContinue={onDriverlessRoutesModalContinue}
        driverlessRoutes={driverlessRoutes}
      />
      <UnallocatedPostcodesModal
        open={!!unallocatedPostcodes}
        onClose={onModalClose}
        onContinue={onUnallocatedPostcodesModalContinue}
        postcodes={unallocatedPostcodes}
        error={isDailyPlan}
      />
      <OverridesConflictsModal
        open={!!overrideConflictsPostcodes}
        onClose={onModalClose}
        postcodes={overrideConflictsPostcodes}
      />
      <SingleWavesPostcodesModal
        open={!!singleWavePostcodes}
        onClose={onModalClose}
        onContinue={onSingleWavePostcodesModalContinue}
        postcodes={singleWavePostcodes}
      />
      <ConfirmModal
        open={confirmCancelModalOpen}
        title={CANCEL_ALL_WARNING_TITLE}
        content={CANCEL_ALL_WARNING}
        onClose={() => setConfirmCancelModalOpen(false)}
        onContinue={() => {
          setConfirmCancelModalOpen(false);
          dispatch(cancelAllEdits());
        }}
      />
    </View>
  );
};

export default Subheader;
